import {
  CREDENTIAL_PROVISIONABLE_QUALIFICATIONS,
  type CredentialProvisionableQualification,
  type CredentialProvisionRequestType,
} from "@clipboard-health/contract-backend-main";
import { type UseModalState } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { Text } from "@src/appV2/redesign/components/Text";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { useEffect } from "react";

import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logError, logEvent } from "../lib/analytics";
import { useCreateWorkerCredentialProvisionRequest } from "./api/useCreateWorkerCredentialProvisionRequest";
import { useChartingCredentialProvisioningConfig } from "./api/useCredentialProvisioningConfig";
import { useCredentialAccountData } from "./hooks/useCredentialAccountData";
import { getRequestType } from "./utils/credentialAccountStatusHelpers";

interface CreateOrResetCredentialsModalProps {
  modalState: UseModalState;
  workerName: string | undefined;
  onAction: () => void;
  workerId: string;
  workplaceId: string;
}

const actionToText: Record<CredentialProvisionRequestType, { label: string; description: string }> =
  {
    create_credentials: {
      label: "Create",
      description:
        "This will begin creating the professional's PCC account and share the credentials once complete.",
    },
    reset_password: {
      label: "Reset",
      description:
        "This will begin resetting the professional's PCC account and share new credentials once complete.",
    },
  };

function isQualificationProvisionable(
  qualification?: string
): qualification is CredentialProvisionableQualification {
  return (
    isDefined(qualification) &&
    (CREDENTIAL_PROVISIONABLE_QUALIFICATIONS as readonly string[]).includes(qualification)
  );
}

export function CreateOrResetCredentialsModal({
  modalState,
  workerName,
  onAction,
  workerId,
  workplaceId,
}: CreateOrResetCredentialsModalProps) {
  const { mutate: createWorkerCredentialRequest, isLoading: isCreatingWorkerCredentialRequest } =
    useCreateWorkerCredentialProvisionRequest();

  const {
    accountStatus,
    defaultQualification,
    isError: isErrorAccountData,
    isLoading: isLoadingAccountData,
  } = useCredentialAccountData({ workerId, workplaceId });

  const requestType = getRequestType(accountStatus);

  const { enabled: isChartingCredentialProvisioningEnabled } =
    useChartingCredentialProvisioningConfig();

  useEffect(() => {
    if (!isLoadingAccountData && !isDefined(defaultQualification)) {
      showErrorToast("Failed to load worker qualification");
    }
  }, [isLoadingAccountData, defaultQualification]);

  if (!isChartingCredentialProvisioningEnabled) {
    return null;
  }

  const actionText = actionToText[requestType];

  const title = isDefined(workerName)
    ? `${actionText.label} PCC Account for ${workerName}`
    : `${actionText.label} PCC Account`;

  const isLoading = isLoadingAccountData || isCreatingWorkerCredentialRequest;

  const isActionDisabled = isErrorAccountData || !isDefined(defaultQualification);

  const handleConfirm = () => {
    if (isQualificationProvisionable(defaultQualification)) {
      logEvent(APP_V2_USER_EVENTS.WORKER_CREDENTIAL_PROVISION_REQUEST_CREATED, {
        workerId,
        workplaceId,
        qualification: defaultQualification,
        requestType,
      });
      createWorkerCredentialRequest(
        {
          workerId,
          workplaceId,
          qualification: defaultQualification,
          requestType,
        },
        {
          onSuccess: () => {
            onAction();
          },
        }
      );
    } else {
      showErrorToast("Unable to provision credentials for worker");
      logError(APP_V2_APP_EVENTS.QUALIFICATION_NOT_PROVISIONABLE_FAILURE, {
        error: "Qualification is not provisionable",
        metadata: {
          workerId,
          workplaceId,
          qualification: defaultQualification,
        },
      });
    }
  };

  return (
    <BerlinThemeProvider>
      <Dialog
        modalState={modalState}
        title={title}
        width={480}
        footerContent={
          <DialogFooter
            primaryAction={{
              label: "Confirm",
              onClick: handleConfirm,
              isLoading,
              disabled: isActionDisabled,
            }}
            secondaryAction={{
              label: "Cancel",
              onClick: modalState.closeModal,
            }}
          />
        }
      >
        <Text variant="body1">{actionText.description}</Text>
      </Dialog>
    </BerlinThemeProvider>
  );
}
